<template>
  <div>
    <Teleport v-if="mounted" to="#header-breadcrumbs">
      <CBreadcrumb v-bind="{ routes }" />
    </Teleport>
    <div
      v-if="!faqList?.length && isLoading"
      class="w-full min-h-[500px] pb-5 flex items-start flex-col md:flex-row gap-3"
    >
      <div
        class="w-full bg-white rounded-xl p-6 flex flex-col gap-4 md:max-w-[30%]"
      >
        <CPreloader
          class="w-full"
          width="100%"
          height="48px"
          loading
          v-for="i in 3"
          :key="i"
        />
      </div>
      <div
        class="w-full bg-white rounded-xl p-5 flex flex-col gap-4 md:max-w-[70%]"
      >
        <CPreloader
          width="100%"
          height="48px"
          loading
          v-for="i in 8"
          :key="i"
        />
      </div>
    </div>
    <div
      v-else
      class="flex flex-col lg:flex-row items-start justify-between gap-5"
    >
      <CFaqSecondVariant
        v-if="faqList?.length"
        @change="handleQuestionClick"
        class="w-full lg:max-w-[30%]"
        :faq="faqList"
      />
      <Transition name="fade" mode="out-in">
        <div class="w-full transition-300" :key="activeTab">
          <div v-for="tab in combinedTabs" :key="tab.id">
            <div
              class="flex flex-col gap-5 transition-300 max-w-[970px]"
              v-if="tab.id == activeTab"
            >
              <div class="bg-white rounded-xl p-5">
                <h4 class="text-dark-100 font-semibold text-2xl">
                  {{ tab.title }}
                </h4>
              </div>
              <div class="!relative bg-white inside-page rounded-xl p-5">
                <div v-html="tab.answer"></div>
                <CButton
                  v-if="tab?.file"
                  @click="downloadFile(tab?.file, tab?.title)"
                  class="!w-auto max-w-[250px]"
                  icon="icon-file-assignment"
                  icon-position="left"
                  :text="$t('download_file')"
                />
              </div>
            </div>
          </div>
          <CNodata
            v-if="!combinedTabs.length && !isLoading"
            image="/images/svg/no-data/no-transaction.svg"
            :title="$t('no_data')"
            subtitle=""
          />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import ApiService from "@/services/ApiService";
import CFaqSecondVariant from "@/components/CFaqSecondVariant.vue";
import CPreloader from "@/components/CPreloader.vue";
import CNodata from "@/components/Common/CNodata.vue";
import CButton from "@/components/Common/CButton.vue";
import CBreadcrumb from "@/components/Common/CBreadcrumb.vue";
import { useMounted } from "@/composables/useMounted";
import { useI18n } from "vue-i18n";
const { locale } = useI18n();

import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
const { pushToDataLayer } = usePushToDataLayer();

const { mounted } = useMounted();
const { t } = useI18n();

const faqList = ref<FaqList[]>([]);
const isLoading = ref(true);
const activeTab = ref();

function getFaqList() {
  isLoading.value = true;
  ApiService.get("/common/faq/")
    .then((res: any) => {
      faqList.value = res?.data?.results;
      activeTab.value = faqList.value[0]?.children?.[0]?.id;
    })
    .catch((err: any) => {
      console.error(err);
    })
    .finally(() => {
      isLoading.value = false;
    });
}
function downloadFile(file: string, title: string) {
  // // Use the fetch API to get the file
  // fetch(file)
  //   .then((response) => {
  //     if (!response.ok) {
  //       throw new Error("Network response was not ok " + response.statusText);
  //     }
  //     return response.blob(); // Convert the response to a Blob
  //   })
  //   .then((blob) => {
  //     // Create a link element, set its href to the Blob URL, and click it programmatically
  //     const url = window.URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.download = title;
  //     document.body.appendChild(link); // Append the link to the body
  //     link.click(); // Simulate a click event
  //     document.body.removeChild(link); // Remove the link from the document
  //     window.URL.revokeObjectURL(url); // Clean up the URL.createObjectURL
  //   })
  //   .catch((error) => {
  //     console.error("There was a problem with the fetch operation:", error);
  //   });
  // Create a temporary anchor element
  const a = document.createElement("a");

  // Set the href attribute to the URL of the file
  a.href = file;
  a.target = "_blank";

  // Set the download attribute to specify the file name
  a.download = title;

  // Append the anchor to the body
  document.body.appendChild(a);

  // Programmatically click the anchor element
  a.click();

  // Remove the anchor element from the document
  document.body.removeChild(a);
}
getFaqList();

const handleQuestionClick = (id: string | number) => {
  if (typeof id === "number") {
    activeTab.value = id;
  } else {
    activeTab.value = id;
  }
};

const combinedTabs = computed<FaqList["children"]>(() => {
  return faqList.value.reduce((acc, tab) => {
    // acc.push(tab);
    if (tab.children) {
      acc.push(...tab.children);
    }
    return acc;
  }, []);
});

interface FaqList {
  id: number;
  title: string;
  children: {
    id: number;
    title: string;
    answer: string;
  }[];
}

const routes = computed(() => [
  {
    name: t("directory"),
    route: "/guide",
  },
]);

onMounted(() => {
  pushToDataLayer("pageview", {
    page: "guide",
  });
});
watch(
  () => locale.value,
  () => {
    getFaqList();
  },
);
</script>

<style>
.inside-page {
  direction: ltr;
  @apply flex flex-col gap-5;
}
.inside-page h1,
.inside-page h2,
.inside-page h3 {
  @apply font-semibold;
}
.inside-page p,
.inside-page span,
.inside-page ul li,
.inside-page ol li {
  @apply text-sm text-dark-100 font-normal;
}
.inside-page img {
  @apply my-3;
}
.inside-page iframe {
  max-width: 650px !important;
}
</style>
